/**
 * Clients hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { Client, CreateClientInput, UpdateClientInput } from '@aelita/shared';

interface ClientFilters {
  page?: number;
  limit?: number;
  status?: string;
  industry?: string;
  search?: string;
}

export function useClients(filters: ClientFilters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  });

  return useQuery({
    queryKey: ['clients', filters],
    queryFn: async () => {
      const response = await api.get<Client[]>(`/clients?${params.toString()}`);
      return response;
    },
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id],
    queryFn: async () => {
      const response = await api.get<Client>(`/clients/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateClientInput) => {
      const response = await api.post<Client>('/clients', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateClientInput }) => {
      const response = await api.put<Client>(`/clients/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client', id] });
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/clients/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useArchiveClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<Client>(`/clients/${id}/archive`, {});
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client', id] });
    },
  });
}

export function useUnarchiveClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<Client>(`/clients/${id}/unarchive`, {});
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client', id] });
    },
  });
}

export function useClientStats(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id, 'stats'],
    queryFn: async () => {
      const response = await api.get<any>(`/clients/${id}/stats`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useClientProjects(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id, 'projects'],
    queryFn: async () => {
      const response = await api.get<any[]>(`/clients/${id}/projects`);
      return response.data;
    },
    enabled: !!id,
  });
}
